import type { CSSProperties, ReactNode } from "react";

/**
 * Gedeelde huisstijl voor de losse mail-link-pagina's (confirm/change/cancel.html).
 * Deze pagina's mounten los in de <body> — geen Shadow DOM, dus geen toegang tot de
 * bk-* classes uit styles.ts (die zijn alleen beschikbaar binnen <bookit-widget>).
 * Zelfde kleurtokens als de e-mails (ReservationMailTemplates.java) voor consistentie.
 */
export const PAGE_COLORS = {
    primary: "#b36a3c",
    danger: "#b41340",
    ok: "#0f8a5f",
    text: "#2a2118",
    textDim: "#6b6259",
    border: "#e8ddd0",
    bg: "#f6f1ea",
    panel2: "#f1e9de",
};

export const PAGE_FONT = "-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,Helvetica,Arial,sans-serif";

// Zelfde publieke logo-URL als de e-mails gebruiken (BookIT-website/public/boekkoek-logo.png).
const LOGO_URL = "https://boekkoek.nl/boekkoek-logo.png";

export function PageShell({ children }: { children: ReactNode }) {
    return (
        <div
            style={{
                minHeight: "100vh",
                background: PAGE_COLORS.bg,
                display: "flex",
                justifyContent: "center",
                padding: "40px 16px",
                boxSizing: "border-box",
                fontFamily: PAGE_FONT,
            }}
        >
            <style>{"@keyframes bk-page-spin{to{transform:rotate(360deg)}}"}</style>
            <div style={{ width: "100%", maxWidth: 480 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
                    <img
                        src={LOGO_URL}
                        width={32}
                        height={32}
                        alt="Boekkoek"
                        style={{ display: "block", borderRadius: 8 }}
                    />
                    <span style={{ fontSize: 17, fontWeight: 800, color: PAGE_COLORS.text, letterSpacing: "-0.01em" }}>
                        Boekkoek
                    </span>
                </div>

                <div
                    style={{
                        background: "#ffffff",
                        border: `1px solid ${PAGE_COLORS.border}`,
                        borderRadius: 16,
                        padding: 28,
                        boxSizing: "border-box",
                    }}
                >
                    {children}
                </div>
            </div>
        </div>
    );
}

export function PageHeading({ children }: { children: ReactNode }) {
    return (
        <h1
            style={{
                margin: "0 0 10px",
                fontSize: 21,
                fontWeight: 800,
                color: PAGE_COLORS.text,
                letterSpacing: "-0.01em",
            }}
        >
            {children}
        </h1>
    );
}

export function PageText({ children, dim }: { children: ReactNode; dim?: boolean }) {
    return (
        <p
            style={{
                margin: "0 0 4px",
                fontSize: 14,
                lineHeight: 1.6,
                color: dim ? PAGE_COLORS.textDim : PAGE_COLORS.text,
            }}
        >
            {children}
        </p>
    );
}

/** Statusicoon voor bevestig/annuleer-uitkomsten — hergebruikt op alle 3 losse pagina's. */
export function StatusIcon({ kind }: { kind: "loading" | "ok" | "error" }) {
    const size = 46;
    if (kind === "loading") {
        return (
            <div
                style={{
                    width: size,
                    height: size,
                    borderRadius: "50%",
                    border: `3px solid ${PAGE_COLORS.border}`,
                    borderTopColor: PAGE_COLORS.primary,
                    marginBottom: 16,
                    animation: "bk-page-spin 0.8s linear infinite",
                }}
            />
        );
    }

    const isOk = kind === "ok";
    return (
        <div
            style={{
                width: size,
                height: size,
                borderRadius: "50%",
                background: isOk ? "rgba(15,138,95,0.14)" : "rgba(180,19,64,0.12)",
                color: isOk ? PAGE_COLORS.ok : PAGE_COLORS.danger,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 22,
                fontWeight: 800,
                marginBottom: 16,
            }}
        >
            {isOk ? "✓" : "!"}
        </div>
    );
}

const buttonBase: CSSProperties = {
    display: "inline-block",
    padding: "12px 22px",
    borderRadius: 10,
    fontWeight: 700,
    fontSize: 14,
    textDecoration: "none",
    cursor: "pointer",
    border: "none",
    boxSizing: "border-box",
};

export function PrimaryButton({
                                   children,
                                   onClick,
                                   disabled,
                                   fullWidth,
                               }: {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    fullWidth?: boolean;
}) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                ...buttonBase,
                width: fullWidth ? "100%" : undefined,
                background: PAGE_COLORS.primary,
                color: "#ffffff",
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "default" : "pointer",
            }}
        >
            {children}
        </button>
    );
}

export function SecondaryButton({
                                     children,
                                     onClick,
                                     disabled,
                                     fullWidth,
                                     danger,
                                 }: {
    children: ReactNode;
    onClick?: () => void;
    disabled?: boolean;
    fullWidth?: boolean;
    danger?: boolean;
}) {
    const color = danger ? PAGE_COLORS.danger : PAGE_COLORS.primary;
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={disabled}
            style={{
                ...buttonBase,
                width: fullWidth ? "100%" : undefined,
                background: "#ffffff",
                color,
                border: `1.5px solid ${color}`,
                opacity: disabled ? 0.6 : 1,
                cursor: disabled ? "default" : "pointer",
            }}
        >
            {children}
        </button>
    );
}
